import * as React from 'react';
import * as styles from './SiteSelector.css';
import { IEnvironment } from '../../interfaces/IEnvironment';
import ContentOrganizerService from '../../services/ContentOrganizerService';
import { IContentOrganizerCreate } from '../../interfaces/IContentOrganizerCreate';
import ContentOrganizerAIService from '../../services/ContentOrganizerAIService';
import { IATextField } from '../textField/TextField';
import { IASiteItem } from './siteItem/SiteItem';
import { IASkeleton } from '../skeleton/Skeleton';

export interface ISiteSelectorProps {
  environment: IEnvironment;
  tenant: string;
  higlightColor: string;
  selectedSites: IContentOrganizerCreate[];
  label?: string;
  style?: React.CSSProperties;
  onSitesSelected: (selectedSites: IContentOrganizerCreate[]) => void;
}

export interface ISiteSelectorState {
  searchText: string;
  isLoadingContentOrganizers: boolean;
  contentOrganizerInstances: Partial<IContentOrganizerCreate>[];
  isLoadingSharepointSitesQuery: boolean;
  sharePointSites: Partial<IContentOrganizerCreate>[];
}

export class IASiteSelector extends React.Component<ISiteSelectorProps, ISiteSelectorState> {

  private queryDelay: NodeJS.Timeout;

  constructor(props: ISiteSelectorProps) {
    super(props);
    this.state = {
      searchText: "",
      isLoadingContentOrganizers: false,
      contentOrganizerInstances: undefined,
      isLoadingSharepointSitesQuery: false,
      sharePointSites: undefined
    };

  }

  componentDidMount(): void {
    this.loadContentOrganizerInstances();
  }

  private loadContentOrganizerInstances(): void {
    this.setState({ isLoadingContentOrganizers: true }, () => {
      ContentOrganizerService.getContentOrganizerInstancesList(this.props.environment, "000", this.props.tenant, true)
        .then((contentOrganizerInstances: Partial<IContentOrganizerCreate>[]) => {
          const messages: Partial<IContentOrganizerCreate> = {
            siteCollection: "IntraActive-Messages",
            title: "Messages"
          };
          contentOrganizerInstances.unshift(messages);
          this.setState({ contentOrganizerInstances });
        })
        .finally(() => {
          this.setState({ isLoadingContentOrganizers: false });
        });
    });
  }

  private searchSharepointSites(): void {
    this.setState({ isLoadingSharepointSitesQuery: true }, () => {
      ContentOrganizerAIService.getSites(this.props.tenant, "000", this.props.environment, this.state.searchText, 100, 0)
        .then((result) => {
          if (result?.items?.length > 0) {
            const items: Partial<IContentOrganizerCreate>[] = result.items.map(item => {
              const siteCollection = item.path.split("/sites/")[1];
              return {
                siteCollection: siteCollection ?? "root",
                title: item.title
              }
            });
            this.setState({ sharePointSites: items });
          } else {
            this.setState({ sharePointSites: [] });
          }
          console.log(result);
        })
        .finally(() => {
          this.setState({ isLoadingSharepointSitesQuery: false });
        });
    });
  }

  private renderSites(contentOrganizerInstances: Partial<IContentOrganizerCreate>[]): JSX.Element[] {
    let selectedSites = this.props.selectedSites;
    return contentOrganizerInstances?.map(contentOrganizer => {
      const selectedIndex: number = (selectedSites && selectedSites?.findIndex(co => co.siteCollection && co.siteCollection === contentOrganizer.siteCollection)) ?? -1;
      const isSelected = selectedIndex >= 0;
      return isSelected ?
        <></>
        :
        <IASiteItem
          highlightColor={this.props.higlightColor}
          onClick={() => {
            if (!selectedSites) {
              selectedSites = [contentOrganizer];
            } else {
              selectedSites.push(contentOrganizer);
            }
            this.props.onSitesSelected(selectedSites);
          }}
          contentOrganizerInstance={contentOrganizer}
          isSelected={false}
        />
    });
  }

  private removeSite(index): void {
    let selectedSites = this.props.selectedSites;
    if (this.props.selectedSites?.length === 1) {
      selectedSites = [];
    } else {
      selectedSites.splice(index, 1);
    }
    this.props.onSitesSelected(selectedSites)
  }

  public render(): JSX.Element {
    return (
      <div
        className={styles.IA_siteSelector}
        style={this.props.style}
      >
        {this.props.label &&
          <div style={{ clear: "both", float: "left", marginTop: 25 }}>{this.props.label}</div>
        }
        <div className={styles.IA_sitesWrapper}>
          {this.props.selectedSites?.length > 0
            ?
            this.props.selectedSites.map((site, index) => {
              return (
                <IASiteItem
                  highlightColor={this.props.higlightColor}
                  onClick={() => {
                    this.removeSite(index);
                  }}
                  contentOrganizerInstance={site}
                  isSelected
                />
              )
            })
            :
            <div>No selected sites</div>
          }
        </div>
        <IATextField
          placeholder="Search all SharePoint sites"
          onChange={(searchText) => {
            this.setState({ searchText });
            clearTimeout(this.queryDelay);
            this.queryDelay = setTimeout(() => {
              if (this.state.searchText) {
                this.searchSharepointSites();
              } else {
                this.setState({ sharePointSites: undefined });
              }
            }, 500);
          }}
          disabled={this.state.isLoadingContentOrganizers}
          text={this.state.searchText}
          borderRadius={5}
          style={{
            marginTop: 0,
            marginBottom: 10
          }}
        />
        {this.state.isLoadingContentOrganizers || this.state.isLoadingSharepointSitesQuery
          ?
          <>
            <div className={styles.IA_sitesWrapper}>
              <IASkeleton
                skeleton={{ height: 54, width: 144, borderRadius: 5, marginTop: 5, marginRight: 5 }}
              />
              <IASkeleton
                skeleton={{ height: 54, width: 105, borderRadius: 5, marginTop: 5, marginRight: 5 }}
              />
              <IASkeleton
                skeleton={{ height: 54, width: 85, borderRadius: 5, marginTop: 5, marginRight: 5 }}
              />
              <IASkeleton
                skeleton={{ height: 54, width: 124, borderRadius: 5, marginTop: 5, marginRight: 5 }}
              />
              <IASkeleton
                skeleton={{ height: 54, width: 85, borderRadius: 5, marginTop: 5, marginRight: 5 }}
              />
            </div>
          </>
          :
          <>
            <div className={styles.IA_sitesWrapper}>
              {this.state.searchText && this.state.sharePointSites
                ?
                <>{this.renderSites(this.state.sharePointSites)}</>
                :
                <>{this.renderSites(this.state.contentOrganizerInstances)}</>
              }
            </div>
          </>
        }
      </div>
    );
  }
}